import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toDate } from '@klareospace/pharos-lib-utils';

import type { CarrierEnablementFormInput } from '../schemas/carrier-enablement.schema';
import { buildCarrierEnablementSchema } from '../schemas/carrier-enablement.schema';
import type { SiteCarrier } from './use-site-carriers-page';
import { useUpdateSiteEnablement } from './use-update-site-enablement';

export interface UseEditEnablementFormReturn {
  form: UseFormReturn<CarrierEnablementFormInput>;
  isPending: boolean;
  onSubmit: (data: CarrierEnablementFormInput) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getFormDefaults(carrier: SiteCarrier | null): CarrierEnablementFormInput {
  return {
    from: toDate(carrier?.validity?.from),
    to: toDate(carrier?.validity?.to),
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useEditEnablementForm(
  carrier: SiteCarrier | null,
  siteId: string,
  spaceId: string,
  onSuccess: () => void,
): UseEditEnablementFormReturn {
  const { t } = useTranslation('settings');
  const updateSiteEnablement = useUpdateSiteEnablement();

  const schema = useMemo(
    () => buildCarrierEnablementSchema(t('sites.settings.carriers.validation.startBeforeEnd')),
    [t],
  );

  const form = useForm<CarrierEnablementFormInput>({
    defaultValues: getFormDefaults(carrier),
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    form.reset(getFormDefaults(carrier));
  }, [carrier, form]);

  function onSubmit(data: CarrierEnablementFormInput): void {
    if (!carrier) return;
    updateSiteEnablement
      .mutateAsync({
        carrierId: carrier.id,
        from: data.from,
        siteId,
        spaceId,
        to: data.to,
      })
      .then(
        () => {
          form.reset();
          onSuccess();
        },
        () => {},
      );
  }

  return { form, isPending: updateSiteEnablement.isPending, onSubmit };
}
